import { OPS_DEPARTMENT_LABEL } from '@utazzo/shared';
import {
  BedDouble,
  CalendarRange,
  Car,
  FileBadge,
  Plane,
  Ship,
  ShieldCheck,
  Sparkles,
  Ticket,
  UtensilsCrossed,
} from 'lucide-react';

import type { OpsActionableKind, OpsCaseItem, OpsDepartment, OpsVasKind } from '@utazzo/shared';

type Icon = React.ComponentType<{ className?: string }>;

/** Component kind + VAS sub-kind -> the ops column it belongs to. */
export const actionableKindOf = (
  kind: OpsCaseItem['kind'],
  vasKind: OpsVasKind | null | undefined,
): OpsActionableKind => {
  switch (kind) {
    case 'FLIGHT':
      return 'flight';
    case 'HOTEL':
      return 'hotel';
    case 'ACTIVITY':
      return 'activity';
    case 'TRANSFER':
      return 'transfer';
    case 'CRUISE':
      return 'cruise';
    default:
      if (vasKind === 'VISA') return 'visa';
      if (vasKind === 'INSURANCE') return 'insurance';
      if (vasKind === 'MEAL') return 'meal';
      if (vasKind === 'FIXED_DEPARTURE') return 'fixed_departure';
      return 'other';
  }
};

export const KIND_META: Record<
  OpsActionableKind,
  { label: string; plural: string; icon: Icon; tone: string }
> = {
  flight: {
    label: 'Flight',
    plural: 'Flights',
    icon: Plane,
    tone: 'bg-sky-50 text-sky-800 ring-sky-200',
  },
  hotel: {
    label: 'Hotel',
    plural: 'Hotels',
    icon: BedDouble,
    tone: 'bg-violet-50 text-violet-800 ring-violet-200',
  },
  activity: {
    label: 'Activity',
    plural: 'Activities',
    icon: Ticket,
    tone: 'bg-amber-50 text-amber-800 ring-amber-200',
  },
  transfer: {
    label: 'Transfer',
    plural: 'Transfers',
    icon: Car,
    tone: 'bg-teal-50 text-teal-800 ring-teal-200',
  },
  cruise: {
    label: 'Cruise',
    plural: 'Cruises',
    icon: Ship,
    tone: 'bg-cyan-50 text-cyan-800 ring-cyan-200',
  },
  visa: {
    label: 'Visa',
    plural: 'Visas',
    icon: FileBadge,
    tone: 'bg-rose-50 text-rose-800 ring-rose-200',
  },
  insurance: {
    label: 'Insurance',
    plural: 'Insurance',
    icon: ShieldCheck,
    tone: 'bg-lime-50 text-lime-800 ring-lime-200',
  },
  meal: {
    label: 'Meal',
    plural: 'Meals',
    icon: UtensilsCrossed,
    tone: 'bg-orange-50 text-orange-800 ring-orange-200',
  },
  fixed_departure: {
    label: 'Fixed departure',
    plural: 'Fixed departures',
    icon: CalendarRange,
    tone: 'bg-fuchsia-50 text-fuchsia-800 ring-fuchsia-200',
  },
  other: {
    label: 'Other',
    plural: 'Other',
    icon: Sparkles,
    tone: 'bg-zinc-50 text-zinc-700 ring-zinc-200',
  },
};

/** The ops desks a converted lead is split across (one case per desk with items). */
export const DEPARTMENT_META: Record<OpsDepartment, { label: string; icon: Icon; tone: string }> = {
  FLIGHT: {
    label: OPS_DEPARTMENT_LABEL.FLIGHT,
    icon: Plane,
    tone: 'bg-sky-50 text-sky-800 ring-sky-200',
  },
  DMC_HOTEL: {
    label: OPS_DEPARTMENT_LABEL.DMC_HOTEL,
    icon: BedDouble,
    tone: 'bg-violet-50 text-violet-800 ring-violet-200',
  },
  VISA: {
    label: OPS_DEPARTMENT_LABEL.VISA,
    icon: FileBadge,
    tone: 'bg-rose-50 text-rose-800 ring-rose-200',
  },
};

/** Singular label for one booking item ("Insurance", "Cruise", ...). */
export const itemKindLabel = (
  kind: OpsCaseItem['kind'],
  vasKind: OpsVasKind | null | undefined,
): string => KIND_META[actionableKindOf(kind, vasKind)].label;

export const itemKindIcon = (
  kind: OpsCaseItem['kind'],
  vasKind: OpsVasKind | null | undefined,
): Icon => KIND_META[actionableKindOf(kind, vasKind)].icon;
